import { type KeyboardEvent, useEffect, useRef, useState } from "react";
import { formatCurrency, toNumber } from "@/lib/formatters";
import { cn } from "@/lib/utils";

interface CurrencyInputProps {
  /** Server-side value as a numeric string, e.g. `"1500"` or `"1500.00"`. */
  value: string;
  /** Fired once on blur (or Enter) when the committed value differs from `value`. */
  onCommit: (next: string) => void;
  /** When false, renders as read-only formatted text. */
  disabled?: boolean;
  className?: string;
  /** Accessible label — Buy Log columns reuse this via an aria-label. */
  ariaLabel?: string;
}

/**
 * Spreadsheet-style currency cell editor.
 *
 * Design rationale:
 * - In the blurred state the cell shows `$1,500.00` so rows are scannable.
 * - On focus we swap to a raw numeric string (`"1500"`) so the user can edit
 *   without fighting formatter characters like `$` and `,`.
 * - On blur we normalize to a two-decimal string and fire `onCommit` once —
 *   only if the value actually changed. Matches how the old raw `<Input>` did
 *   it but without the double commits from synthetic change events.
 * - Enter commits and blurs so a user can tab through rows quickly.
 *
 * Does NOT handle tab/arrow navigation across cells — keeping the component
 * small; native Tab order handles it fine since the inputs sit next to each
 * other in the DOM.
 */
export function CurrencyInput({
  value,
  onCommit,
  disabled,
  className,
  ariaLabel,
}: CurrencyInputProps) {
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Re-sync when the server value changes externally (another tab saved,
  // mutation refetch, etc). Only when not focused to avoid clobbering typing.
  useEffect(() => {
    if (!focused) setDraft(value);
  }, [value, focused]);

  function commit() {
    const trimmed = draft.trim();
    // Empty → "0"; otherwise normalize to a numeric string with up to 2 dp.
    const normalized = trimmed === "" ? "0" : toNumber(trimmed).toFixed(2).replace(/\.00$/, "");
    // Compare normalized to server value — account for `"0"` vs `"0.00"` etc.
    if (toNumber(normalized) !== toNumber(value)) {
      onCommit(normalized);
    }
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      inputRef.current?.blur();
    } else if (e.key === "Escape") {
      // Revert to server value and blur.
      setDraft(value);
      inputRef.current?.blur();
    }
  }

  if (disabled) {
    return <span className="text-sm text-gray-900">{formatCurrency(value)}</span>;
  }

  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="decimal"
      aria-label={ariaLabel}
      value={focused ? draft : formatDisplay(value)}
      onFocus={(e) => {
        setFocused(true);
        setDraft(stripFormatting(value));
        // Select all so typing replaces; feels native for spreadsheet users.
        e.target.select();
      }}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        setFocused(false);
        commit();
      }}
      onKeyDown={handleKeyDown}
      className={cn(
        "h-8 w-28 rounded border border-transparent bg-transparent px-2 text-right text-sm",
        "hover:border-gray-300",
        "focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900 focus:bg-white",
        className,
      )}
    />
  );
}

function stripFormatting(value: string): string {
  // On focus, show a bare numeric string — drop trailing ".00" and any
  // accidental $/, left over from legacy storage.
  const n = toNumber(value);
  if (n === 0) return "";
  const s = n.toFixed(2);
  return s.endsWith(".00") ? s.slice(0, -3) : s;
}

function formatDisplay(value: string): string {
  // Blurred-state display. `formatCurrency` returns `"--"` for empty/null;
  // coerce those to `"$0"` here so the cell always looks like a value cell.
  const n = toNumber(value);
  return formatCurrency(n, { precise: true });
}
